<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { Calculator, calculatorMinSize, DraggableModal } from '@oat-sa-private/ui-components';
    import { __, getDefaultRemSizePx } from '@oat-sa-private/ui-core';
    import { testLayoutStore, setActiveTool } from '../../../layout/testLayoutStore.js';

    // Minimal size that fits all calculator variants
    const defaultPxInRem = getDefaultRemSizePx();
    const minWidth = calculatorMinSize.width * defaultPxInRem;
    const minHeight = calculatorMinSize.height * defaultPxInRem;

    /**
     * @typedef {Object} Props
     * @property {String} [type] - the type of calculator to present, it can be either: 'basic', 'bodmas', 'scientific'
     * @property {Number} [decimals] - the number of decimals to present after the dot
     * @property {Number} [top] - the top offset of the containing DraggableModal (px)
     * @property {Number} [left] - the left offset of the containing DraggableModal (px)
     * @property {Number} [width] - the width of the containing DraggableModal (rem)
     * @property {Number} [height] - the height of the containing DraggableModal (rem)
     * @property {Boolean} [open] - whether the calculator is visible
     * @property {Function} onClose
     * @property {Function} onResize
     * @property {Function} onMove
     */
    /** @type {Props} */
    let {
        type = 'basic',
        decimals = 5,
        top = 0,
        left = 0,
        width = minWidth,
        height = minHeight,
        open = true,
        onClose,
        onResize,
        onMove
    } = $props();

    const title = __('Calculator');
</script>

<style>
    .calculator-wrapper {
        letter-spacing: 0;
        word-spacing: 0;
        position: fixed;
        z-index: calc(var(--layer-5) - 2);

        & :global(.header) {
            white-space: nowrap;
            overflow: hidden;
        }

        &.active {
            z-index: calc(var(--layer-5));
        }
    }
</style>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
    class="calculator-wrapper"
    class:active={$testLayoutStore.activeTool === 'calculator'}
    hidden={!open}
    onmousedown={() => setActiveTool('calculator')}>
    <DraggableModal
        {title}
        {top}
        {left}
        {width}
        {height}
        {minWidth}
        {minHeight}
        noRemScaling={true}
        on:close={onClose}
        on:resize={onResize}
        on:move={onMove}>
        <Calculator {type} {decimals} on:mount={() => setActiveTool('calculator')} on:result on:error />
    </DraggableModal>
</div>
